import ELK from 'elkjs/lib/elk.bundled.js';
import type { ElkNode } from 'elkjs';
import type { CSSProperties } from 'react';
import type { Edge, Node, Position } from 'reactflow';
import type { Cluster, ClusterVocab } from '../../types/cluster';
import type { ClusterRegionNodeData } from './ClusterRegionNode';
import { TIER_BAND_PAD, TIER_EDGE_GAP, TIER_ROW_GAP, type TierBand } from './TierLanes';
import { STEP_NUMBER_GUTTER, STEP_NUMBER_MARGIN, STEP_NUMBER_SIZE } from './StepNumberNode';

const elk = new ELK();

export const UNSORTED_CLUSTER_NODE_ID = '__cluster_unsorted__';

const DEFAULT_NODE_WIDTH = 180;
const DEFAULT_NODE_HEIGHT = 100;
const CLUSTER_FALLBACK_MIN_WIDTH = 240;
// Minimum region height floor (degenerate/empty clusters). Kept below the
// natural height of a single compact-card row (~84px: header + 42px card +
// thin edge pads) so a real single-row cluster uses its computed height and
// doesn't get inflated with dead space at the bottom.
const DEFAULT_CLUSTER_HEIGHT = 72;
// Vertical gap between inter-cluster tiers (tiered mode). Each tier's clusters
// are top-aligned, and the next tier starts this far below the BOTTOM of the
// tallest cluster in the tier above — so tiers never overlap. Compact, to match
// the build view's tight inter-box gaps now that cards/boxes are compact.
const OUTER_TIER_GAP = 28;

// Graph layer z-order. The connector edges sit in the MIDDLE of the stack:
// above the cluster-box body + zebra tier shading (so the lines never read as
// faded through the translucent zebra) but below the box header and the node
// cards (so the lines never paint over the title text or the card faces).
//
//   regionBody (1)  <  edgeResting (2)  <  region/header (3)  <  card (4)
//
// Build view splits each cluster box into TWO React Flow nodes to straddle the
// edge layer: a BODY node (border + fill + zebra) at `regionBody`, and a HEADER
// child at `region`. This split is forced by React Flow v11, which clamps a
// child's z to max(parent.z, child.z) — a child can never render BELOW its
// parent, so the low part (zebra) must be the parent and the high part (header)
// the child. Research view keeps a single combined node at `region`.
//
// Hosts lift edges to `edgeElevated` (above the cards) when they need the
// connectors to read clearly on top — the edges CONNECTED to a selected/
// highlighted node or cluster jump here so it's crystal clear what links to
// what; a future drag can reuse the same layer. On deselect they return to
// `edgeResting`. The invariant that matters is the ORDER, not the literals.
export const GRAPH_LAYER = {
  regionBody: 1,
  edgeResting: 2,
  region: 3,
  card: 4,
  edgeElevated: 10,
} as const;

// Height of the build-view cluster-box header strip (2px accent bar + 26px
// header row). The header is emitted as a child node of this height pinned to
// the top of the body; cards lay out below it (clusterLabelHeight reserves it).
export const HEADER_LAYER_HEIGHT = 28;

export interface ClusterPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ClusterLayoutRawNode<TData = unknown> {
  id: string;
  width: number;
  height: number;
  cluster_id: string | null;
  type?: string;
  data: TData;
  selected?: boolean;
  hidden?: boolean;
  className?: string;
  sourcePosition?: Position;
  targetPosition?: Position;
  style?: CSSProperties;
}

export interface ClusterLayoutRawEdge<TData = unknown> {
  id: string;
  source: string;
  target: string;
  type?: string;
  data?: TData;
  animated?: boolean;
  hidden?: boolean;
  className?: string;
  style?: CSSProperties;
}

export interface BuildClusteredElkGraphOptions {
  padding?: ClusterPadding;
  nodeNodeSpacing?: number;
  clusterLabelHeight?: number;
  elkOptions?: Record<string, string>;
  vocab?: ClusterVocab;
  /**
   * Optional per-region accent color (CSS color string, including
   * `var(--token)` refs). Used by the build view to tint each cluster
   * column with the dominant node-category color. If omitted or no
   * entry for a region, the region uses its status-based default.
   */
  accentByRegionId?: Record<string, string>;
  /**
   * Controls how nodes are arranged WITHIN each cluster region.
   * - 'flow' (default): nodes laid out left-to-right (elk.direction
   *   RIGHT) with a synthetic chain forcing a single horizontal row,
   *   regardless of real dependencies.
   * - 'tiered': nodes laid out top-to-bottom (elk.direction DOWN) by
   *   their real intra-cluster dependency edges, so dependent nodes
   *   stack into tiers. Peers with no edge between them share a tier
   *   (one row), so peer-only clusters still render as a single row.
   * Only affects the multi-region interior pass; single-region clusters
   * already lay out by real edges in DOWN direction.
   */
  interiorLayout?: 'flow' | 'tiered';
  /**
   * Controls how cluster regions are anchored horizontally in the outer
   * (region-level) pass.
   * - 'center' (default): regions are centered on a shared vertical axis
   *   (ELK's Brandes-Köpf BALANCED placement), so narrower regions float
   *   in the middle.
   * - 'left': every region shares the same left edge (snapped to the
   *   leftmost region after layout) and grows rightward. Cross-region
   *   edges are drawn by ReactFlow from node positions, so this snap is
   *   safe — no edge re-routing needed.
   * Build (ProcessDAG) opts into 'left'; research keeps the default.
   */
  outerAlignment?: 'center' | 'left';
  /**
   * How cluster regions are arranged among each other (outer pass).
   * - 'sequence' (default): a synthetic chain forces a single vertical column in
   *   topological order — dependency order, not depth tiers.
   * - 'tiered': regions tier by longest-path dependency depth from the source;
   *   same-depth clusters share a tier, the QUESTION centers on top. Research
   *   (EvidenceBoard) opts in. (See the BAR-173 plan for the full rationale.)
   */
  interClusterLayout?: 'sequence' | 'tiered';
  /**
   * When true, emit a big light-grey "step number" node in the left gutter for
   * each cluster region, numbering them top→bottom (1, 2, 3…). Build view opts
   * in; research leaves it off (clusters there aren't sequential steps).
   */
  stepNumbers?: boolean;
}

interface Region {
  id: string;
  cluster: Cluster | null;
  nodes: Array<ClusterLayoutRawNode<unknown>>;
  isUnsorted: boolean;
}

interface RegionLayout {
  region: Region;
  width: number;
  height: number;
  children: Array<{ id: string; x: number; y: number }>;
}

const DEFAULT_PADDING: ClusterPadding = {
  top: 24,
  right: 16,
  bottom: 16,
  left: 16,
};

const DEFAULT_VOCAB: ClusterVocab = {
  singular: 'cluster',
  plural: 'clusters',
};

function clusterPaddingToElk(padding: ClusterPadding): string {
  return `[top=${padding.top},left=${padding.left},bottom=${padding.bottom},right=${padding.right}]`;
}

const TIER_GROUP_TOLERANCE = 8;

/**
 * Group a region's laid-out children into dependency tiers by their
 * y-position (ELK places same-tier nodes on the same row), then return one
 * full-row band per tier. Bands are contiguous — they tile the region's
 * interior from top (0) to `regionHeight`, split at the midpoints between
 * adjacent tier rows — so the renderer can shade alternate rows (zebra) with
 * no gaps. `depth` is the 0-based tier index from the top.
 */
function computeTierBands(
  region: Region,
  children: Array<{ id: string; x: number; y: number }>,
  regionHeight: number,
  headerHeight: number,
): TierBand[] {
  if (children.length === 0) {
    return [];
  }
  const heightById = new Map(
    region.nodes.map((node) => [node.id, node.height || DEFAULT_NODE_HEIGHT]),
  );
  const sorted = [...children].sort((a, b) => a.y - b.y);
  const tiers: Array<{ refY: number; top: number; bottom: number }> = [];
  sorted.forEach((child) => {
    const height = heightById.get(child.id) ?? DEFAULT_NODE_HEIGHT;
    const last = tiers[tiers.length - 1];
    if (last && Math.abs(child.y - last.refY) <= TIER_GROUP_TOLERANCE) {
      last.top = Math.min(last.top, child.y);
      last.bottom = Math.max(last.bottom, child.y + height);
    } else {
      tiers.push({ refY: child.y, top: child.y, bottom: child.y + height });
    }
  });

  // Contiguous boundaries: region top (0) → midpoint between each adjacent
  // tier's node rows → region bottom. Each band fills the slot between two
  // boundaries, so alternating bands tile the interior cleanly.
  const boundaries: number[] = [0];
  for (let i = 1; i < tiers.length; i += 1) {
    boundaries.push((tiers[i - 1].bottom + tiers[i].top) / 2);
  }
  boundaries.push(Math.max(regionHeight, tiers[tiers.length - 1].bottom));

  return tiers.map((_tier, depth) => ({
    top: boundaries[depth],
    height: boundaries[depth + 1] - boundaries[depth],
    depth,
    // Corner number anchors at the band top, but the first tier's band starts
    // at 0 (behind the header) — clamp it below the header so the number lands
    // in the visible top-left corner.
    numberTop: Math.max(boundaries[depth], headerHeight),
  }));
}

function sortClustersTopologically(
  clusters: Cluster[],
  rawNodes: Array<ClusterLayoutRawNode<unknown>>,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
): Cluster[] {
  const clusterIds = new Set(clusters.map((cluster) => cluster.cluster_id));
  const nodeCluster = new Map<string, string | null>();
  rawNodes.forEach((node) => {
    nodeCluster.set(node.id, node.cluster_id && clusterIds.has(node.cluster_id) ? node.cluster_id : null);
  });

  const outgoing = new Map<string, Set<string>>();
  const indegree = new Map<string, number>();
  clusters.forEach((cluster) => {
    outgoing.set(cluster.cluster_id, new Set());
    indegree.set(cluster.cluster_id, 0);
  });

  rawEdges.forEach((edge) => {
    const sourceCluster = nodeCluster.get(edge.source);
    const targetCluster = nodeCluster.get(edge.target);
    if (!sourceCluster || !targetCluster || sourceCluster === targetCluster) {
      return;
    }

    const targets = outgoing.get(sourceCluster);
    if (targets && !targets.has(targetCluster)) {
      targets.add(targetCluster);
      indegree.set(targetCluster, (indegree.get(targetCluster) || 0) + 1);
    }
  });

  const originalIndex = new Map(clusters.map((cluster, index) => [cluster.cluster_id, index]));
  const queue = clusters
    .filter((cluster) => (indegree.get(cluster.cluster_id) || 0) === 0)
    .sort((a, b) => (originalIndex.get(a.cluster_id) || 0) - (originalIndex.get(b.cluster_id) || 0));
  const result: Cluster[] = [];
  const seen = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (seen.has(current.cluster_id)) {
      continue;
    }
    seen.add(current.cluster_id);
    result.push(current);

    const nextIds = Array.from(outgoing.get(current.cluster_id) || [])
      .sort((a, b) => (originalIndex.get(a) || 0) - (originalIndex.get(b) || 0));
    nextIds.forEach((nextId) => {
      const nextIndegree = (indegree.get(nextId) || 0) - 1;
      indegree.set(nextId, nextIndegree);
      if (nextIndegree === 0) {
        const nextCluster = clusters.find((cluster) => cluster.cluster_id === nextId);
        if (nextCluster) {
          queue.push(nextCluster);
        }
      }
    });
  }

  clusters.forEach((cluster) => {
    if (!seen.has(cluster.cluster_id)) {
      result.push(cluster);
    }
  });

  return result;
}

function buildRegions(
  rawNodes: Array<ClusterLayoutRawNode<unknown>>,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  clusters: Cluster[],
): Region[] {
  const knownClusterIds = new Set(clusters.map((cluster) => cluster.cluster_id));
  const clusterIdByMemberNodeId = new Map<string, string>();
  clusters.forEach((cluster) => {
    cluster.member_node_ids.forEach((nodeId) => {
      if (!clusterIdByMemberNodeId.has(nodeId)) {
        clusterIdByMemberNodeId.set(nodeId, cluster.cluster_id);
      }
    });
  });
  const nodesByRegion = new Map<string, Array<ClusterLayoutRawNode<unknown>>>();
  clusters.forEach((cluster) => nodesByRegion.set(cluster.cluster_id, []));
  nodesByRegion.set(UNSORTED_CLUSTER_NODE_ID, []);

  rawNodes.forEach((node) => {
    const explicitClusterId = node.cluster_id && knownClusterIds.has(node.cluster_id)
      ? node.cluster_id
      : null;
    const memberClusterId = clusterIdByMemberNodeId.get(node.id);
    const regionId = explicitClusterId || memberClusterId || UNSORTED_CLUSTER_NODE_ID;
    nodesByRegion.get(regionId)?.push(node);
  });

  if (clusters.length === 0) {
    return [{
      id: UNSORTED_CLUSTER_NODE_ID,
      cluster: null,
      nodes: nodesByRegion.get(UNSORTED_CLUSTER_NODE_ID) || [],
      isUnsorted: true,
    }];
  }

  const clusterRegions: Region[] = sortClustersTopologically(clusters, rawNodes, rawEdges).map((cluster) => ({
    id: cluster.cluster_id,
    cluster,
    nodes: nodesByRegion.get(cluster.cluster_id) || [],
    isUnsorted: false,
  }));
  const unsortedRegion: Region = {
    id: UNSORTED_CLUSTER_NODE_ID,
    cluster: null,
    nodes: nodesByRegion.get(UNSORTED_CLUSTER_NODE_ID) || [],
    isUnsorted: true,
  };

  // Where does the unsorted region sit in the top→bottom chain? Map every node
  // to its region, then look at the unsorted region's cross-region edges. If it
  // only feeds clusters and nothing feeds it (a pure source — e.g. the research
  // QUESTION root), it belongs at the TOP; otherwise it stays last (the default
  // for orphan/leaf/mixed nodes, so they don't jump to the top).
  const nodeRegionId = new Map<string, string>();
  rawNodes.forEach((node) => {
    const explicitClusterId = node.cluster_id && knownClusterIds.has(node.cluster_id)
      ? node.cluster_id
      : null;
    const memberClusterId = clusterIdByMemberNodeId.get(node.id);
    nodeRegionId.set(
      node.id,
      explicitClusterId || memberClusterId || UNSORTED_CLUSTER_NODE_ID,
    );
  });
  let unsortedOutgoing = 0;
  let unsortedIncoming = 0;
  rawEdges.forEach((edge) => {
    const sourceRegion = nodeRegionId.get(edge.source);
    const targetRegion = nodeRegionId.get(edge.target);
    if (!sourceRegion || !targetRegion || sourceRegion === targetRegion) {
      return;
    }
    if (sourceRegion === UNSORTED_CLUSTER_NODE_ID) unsortedOutgoing += 1;
    if (targetRegion === UNSORTED_CLUSTER_NODE_ID) unsortedIncoming += 1;
  });
  // NOTE (limitation): the unsorted region is one positional block, so this is
  // an all-or-nothing decision. A graph that puts BOTH an unclustered source
  // (question) AND an unclustered sink (conclusion) in the same unsorted block
  // would have unsortedIncoming > 0 and fall back to "last". Today's research
  // graphs cluster their conclusion, so the unsorted block is the pure-source
  // question — handled correctly. Revisit (split the unsorted block) only if a
  // real graph hits the mixed case.
  const unsortedIsSource = unsortedRegion.nodes.length > 0 && unsortedOutgoing > 0 && unsortedIncoming === 0;

  const regions: Region[] = unsortedIsSource
    ? [unsortedRegion, ...clusterRegions]
    : [...clusterRegions, unsortedRegion];

  return regions.filter((region) => !(region.isUnsorted && region.nodes.length === 0));
}

export function buildClusterInteriorLayoutOptions(params: {
  padding: ClusterPadding;
  nodeNodeSpacing: number;
  elkOptions?: Record<string, string>;
  interiorLayout?: 'flow' | 'tiered';
}): Record<string, string> {
  const callerOptions = { ...(params.elkOptions || {}) };
  delete callerOptions['elk.direction'];
  delete callerOptions['elk.hierarchyHandling'];
  delete callerOptions['elk.padding'];
  // ELK_OPTIONS_MODELING carries a broad `elk.spacing.nodeNode` (80) meant for
  // the un-clustered DAG; left in, it overrides our tighter intra-cluster card
  // spacing below (the cards drift far apart horizontally). Strip it so
  // params.nodeNodeSpacing wins — same as direction/padding above.
  delete callerOptions['elk.spacing.nodeNode'];

  return {
    'elk.algorithm': 'layered',
    'elk.spacing.nodeNode': String(params.nodeNodeSpacing),
    'elk.layered.spacing.nodeNodeBetweenLayers': '40',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    'elk.separateConnectedComponents': 'false',
    ...callerOptions,
    'elk.direction': params.interiorLayout === 'tiered' ? 'DOWN' : 'RIGHT',
    // Tiered bars hug their node row (node + 2·TIER_BAND_PAD); set the
    // between-layer spacing so the gap between adjacent bars lands at
    // TIER_ROW_GAP of body bg.
    ...(params.interiorLayout === 'tiered'
      ? { 'elk.layered.spacing.nodeNodeBetweenLayers': String(TIER_ROW_GAP + 2 * TIER_BAND_PAD) }
      : {}),
    'elk.padding': clusterPaddingToElk(params.padding),
  };
}

function buildClusterInteriorElkGraph(
  region: Region,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'elkOptions' | 'interiorLayout'>,
): ElkNode {
  const headerHeight = Math.max(options.padding.top, options.clusterLabelHeight + 8);
  // Tiered mode: pad enough that the hugging bars (node + 2·TIER_BAND_PAD) sit
  // fully below the header and above the bottom edge, leaving a thin
  // TIER_EDGE_GAP of body bg at top and bottom (so the first/last bars don't
  // touch the walls and their nodes read as centered).
  const padding = options.interiorLayout === 'tiered'
    ? {
        ...options.padding,
        // Hug: reserve just the rendered header (no extra gap below it) plus a
        // thin edge/band pad, so the first card sits close under the header and
        // the last card close to the bottom edge.
        top: Math.max(options.padding.top, options.clusterLabelHeight) + TIER_EDGE_GAP + TIER_BAND_PAD,
        bottom: TIER_EDGE_GAP + TIER_BAND_PAD,
      }
    : { ...options.padding, top: headerHeight };
  const nodeIds = new Set(region.nodes.map((node) => node.id));
  const intraRegionEdges = rawEdges
    .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
    .map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    }));
  // In 'tiered' mode, real intra-cluster edges drive the layered placement
  // (dependent nodes stack into tiers). The synthetic chain is only used by
  // 'flow' mode to force isolated nodes into a single horizontal row.
  const orderEdges = options.interiorLayout === 'tiered'
    ? []
    : region.nodes.slice(0, -1).map((node, index) => ({
        id: `__cluster_interior_order_${region.id}_${index}`,
        sources: [node.id],
        targets: [region.nodes[index + 1].id],
      }));

  return {
    id: region.id,
    layoutOptions: buildClusterInteriorLayoutOptions({
      padding,
      nodeNodeSpacing: options.nodeNodeSpacing,
      elkOptions: options.elkOptions,
      interiorLayout: options.interiorLayout,
    }),
    children: region.nodes.map((node) => ({
      id: node.id,
      width: node.width || DEFAULT_NODE_WIDTH,
      height: node.height || DEFAULT_NODE_HEIGHT,
    })),
    edges: [...intraRegionEdges, ...orderEdges],
  };
}

async function layoutRegionInterior(
  region: Region,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'elkOptions' | 'interiorLayout'>,
): Promise<RegionLayout> {
  const elkGraph = buildClusterInteriorElkGraph(region, rawEdges, options);
  const laidOut = await elk.layout(elkGraph);

  return {
    region,
    width: Math.max(CLUSTER_FALLBACK_MIN_WIDTH, laidOut.width || CLUSTER_FALLBACK_MIN_WIDTH),
    height: Math.max(DEFAULT_CLUSTER_HEIGHT, laidOut.height || DEFAULT_CLUSTER_HEIGHT),
    children: (laidOut.children || []).map((child) => ({
      id: child.id,
      x: child.x || 0,
      y: child.y || 0,
    })),
  };
}

function buildOuterClusterElkGraph(
  regionLayouts: RegionLayout[],
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'elkOptions' | 'interClusterLayout'>,
  // Region→depth map (longest-path-from-source), computed once by the caller and
  // shared with the vertical tier-band stacking. Null in 'sequence' mode.
  regionDepths: Map<string, number> | null,
): ElkNode {
  const nodeToRegion = new Map<string, string>();
  regionLayouts.forEach(({ region }) => {
    region.nodes.forEach((node) => nodeToRegion.set(node.id, region.id));
  });

  const crossRegionEdges = rawEdges.flatMap((edge) => {
    const sourceRegion = nodeToRegion.get(edge.source);
    const targetRegion = nodeToRegion.get(edge.target);
    if (!sourceRegion || !targetRegion || sourceRegion === targetRegion) {
      return [];
    }
    return [{
      id: edge.id,
      sources: [sourceRegion],
      targets: [targetRegion],
    }];
  });
  const tiered = options.interClusterLayout === 'tiered';

  // 'sequence' (default): chain regions into one vertical column in topological
  // order via synthetic order-edges.
  const orderEdges = tiered
    ? []
    : regionLayouts.slice(0, -1).map((layout, index) => ({
        id: `__cluster_order_${layout.region.id}_${regionLayouts[index + 1].region.id}`,
        sources: [layout.region.id],
        targets: [regionLayouts[index + 1].region.id],
      }));

  // 'tiered': put each region on a tier = its longest-path dependency DEPTH FROM
  // THE SOURCE (the unclustered QUESTION), so the question's direct children all
  // sit on the top tier regardless of how far they are from the conclusion, and
  // same-depth clusters share a tier. ELK's own layering (network-simplex) does
  // NOT do this — it pulls nodes toward edge-length-min — so we compute the
  // depth ourselves and pin it with ELK partitioning.
  const partitionByRegion = tiered ? regionDepths : null;

  return {
    id: 'root',
    layoutOptions: tiered
      ? { ...baseElkOptions(options), 'elk.partitioning.activate': 'true' }
      : baseElkOptions(options),
    children: regionLayouts.map((layout) => ({
      id: layout.region.id,
      width: layout.width,
      height: layout.height,
      ...(partitionByRegion
        ? { layoutOptions: { 'elk.partitioning.partition': String(partitionByRegion.get(layout.region.id) ?? 0) } }
        : {}),
    })),
    edges: [...crossRegionEdges, ...orderEdges],
  };
}

/**
 * Longest-path depth of each region from the source(s), over the cluster-level
 * dependency edges. Sources (no incoming cross-region edge — e.g. the
 * unclustered QUESTION) are depth 0; every other region is 1 + the max depth of
 * its predecessors. Cycles are broken by iterating to a fixed point with a cap.
 */
function computeRegionDepths(
  regionLayouts: RegionLayout[],
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
): Map<string, number> {
  const nodeToRegion = new Map<string, string>();
  regionLayouts.forEach(({ region }) => region.nodes.forEach((n) => nodeToRegion.set(n.id, region.id)));
  const ids = regionLayouts.map((l) => l.region.id);
  // Dedupe predecessors per region (multiple edges between the same two regions
  // shouldn't be counted more than once).
  const preds = new Map<string, Set<string>>(ids.map((id) => [id, new Set<string>()]));
  rawEdges.forEach((e) => {
    const s = nodeToRegion.get(e.source);
    const t = nodeToRegion.get(e.target);
    if (s && t && s !== t && preds.has(t)) preds.get(t)!.add(s);
  });
  const depth = new Map<string, number>(ids.map((id) => [id, 0]));
  // Relax to a fixed point (DAG converges in ≤ N passes; cap guards cycles).
  for (let pass = 0; pass < ids.length; pass += 1) {
    let changed = false;
    ids.forEach((id) => {
      const p = preds.get(id)!;
      const next = p.size ? Math.max(...Array.from(p, (q) => depth.get(q) ?? 0)) + 1 : 0;
      if (next !== depth.get(id)) { depth.set(id, next); changed = true; }
    });
    if (!changed) break;
  }
  return depth;
}

function baseElkOptions(
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'elkOptions'>,
  padding?: ClusterPadding,
): Record<string, string> {
  const callerOptions = options.elkOptions || {};
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.spacing.nodeNode': String(options.nodeNodeSpacing),
    'elk.layered.spacing.nodeNodeBetweenLayers': '40',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    'elk.separateConnectedComponents': 'false',
    ...callerOptions,
    'elk.padding': padding
      ? clusterPaddingToElk(padding)
      : callerOptions['elk.padding'] || '[top=40,left=40,bottom=40,right=40]',
  };
}

async function layoutSingleRegion(
  region: Region,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'elkOptions' | 'vocab' | 'accentByRegionId' | 'interiorLayout'>,
): Promise<{ rfNodes: Node[]; rfEdges: Edge[] }> {
  const headerHeight = Math.max(options.padding.top, options.clusterLabelHeight + 8);
  // Tiered mode: pad enough that the hugging bars (node + 2·TIER_BAND_PAD) sit
  // fully below the header and above the bottom edge, leaving a thin
  // TIER_EDGE_GAP of body bg at top and bottom (so the first/last bars don't
  // touch the walls and their nodes read as centered).
  const padding = options.interiorLayout === 'tiered'
    ? {
        ...options.padding,
        // Hug: reserve just the rendered header (no extra gap below it) plus a
        // thin edge/band pad, so the first card sits close under the header and
        // the last card close to the bottom edge.
        top: Math.max(options.padding.top, options.clusterLabelHeight) + TIER_EDGE_GAP + TIER_BAND_PAD,
        bottom: TIER_EDGE_GAP + TIER_BAND_PAD,
      }
    : { ...options.padding, top: headerHeight };
  const elkGraph: ElkNode = {
    id: 'root',
    layoutOptions: baseElkOptions(options, padding),
    children: region.nodes.map((node) => ({
      id: node.id,
      width: node.width || DEFAULT_NODE_WIDTH,
      height: node.height || DEFAULT_NODE_HEIGHT,
    })),
    edges: rawEdges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const laidOut = await elk.layout(elkGraph);
  const width = Math.max(CLUSTER_FALLBACK_MIN_WIDTH, laidOut.width || CLUSTER_FALLBACK_MIN_WIDTH);
  const height = Math.max(DEFAULT_CLUSTER_HEIGHT, laidOut.height || DEFAULT_CLUSTER_HEIGHT);
  const rfNodes: Node[] = [];
  if (!region.isUnsorted) {
    const tierBands = options.interiorLayout === 'tiered'
      ? computeTierBands(
          region,
          (laidOut.children || []).map((child) => ({
            id: child.id,
            x: child.x || 0,
            y: child.y || 0,
          })),
          height,
          headerHeight,
        )
      : undefined;
    rfNodes.push(...regionToReactFlowNode(
      region,
      40,
      40,
      width,
      height,
      options.vocab || DEFAULT_VOCAB,
      [region],
      options.accentByRegionId?.[region.id],
      tierBands,
    ));
  }

  (laidOut.children || []).forEach((laidNode) => {
    const rawNode = region.nodes.find((candidate) => candidate.id === laidNode.id);
    if (!rawNode) {
      return;
    }
    if (region.isUnsorted) {
      rfNodes.push(rawNodeToReactFlowNode(rawNode, undefined, {
        x: 40 + (laidNode.x || 0),
        y: 40 + (laidNode.y || 0),
      }));
    } else {
      rfNodes.push(rawNodeToReactFlowNode(rawNode, region.id, {
        x: laidNode.x || 0,
        y: laidNode.y || 0,
      }));
    }
  });

  return {
    rfNodes,
    rfEdges: rawEdges.map(rawEdgeToReactFlowEdge),
  };
}

function fallbackLayout(
  regions: Region[],
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  options: Required<Pick<BuildClusteredElkGraphOptions, 'padding' | 'nodeNodeSpacing' | 'clusterLabelHeight'>> & Pick<BuildClusteredElkGraphOptions, 'vocab' | 'accentByRegionId'>,
): { rfNodes: Node[]; rfEdges: Edge[] } {
  const rfNodes: Node[] = [];
  let cursorY = 40;
  const regionGap = options.nodeNodeSpacing;
  const nodeGap = 48;
  // Error-path grid fallback — lays nodes in plain rows; no tiering.
  const padding = {
    ...options.padding,
    top: Math.max(options.padding.top, options.clusterLabelHeight + 8),
  };

  regions.forEach((region) => {
    const nodeWidths = region.nodes.map((node) => node.width || DEFAULT_NODE_WIDTH);
    const maxNodeHeight = Math.max(DEFAULT_NODE_HEIGHT, ...region.nodes.map((node) => node.height || DEFAULT_NODE_HEIGHT));
    const rowWidth = nodeWidths.reduce((sum, width) => sum + width, 0)
      + Math.max(0, region.nodes.length - 1) * nodeGap;
    const width = Math.max(
      CLUSTER_FALLBACK_MIN_WIDTH,
      padding.left + padding.right + rowWidth,
    );
    const height = Math.max(
      DEFAULT_CLUSTER_HEIGHT,
      padding.top + padding.bottom + maxNodeHeight,
    );

    if (!region.isUnsorted) {
      rfNodes.push(...regionToReactFlowNode(
        region,
        40,
        cursorY,
        width,
        height,
        options.vocab || DEFAULT_VOCAB,
        regions,
        options.accentByRegionId?.[region.id],
      ));
    }

    let childX = padding.left;
    region.nodes.forEach((node, index) => {
      if (region.isUnsorted) {
        rfNodes.push(rawNodeToReactFlowNode(
          node,
          undefined,
          {
            x: 40 + childX,
            y: cursorY + padding.top,
          },
        ));
      } else {
        rfNodes.push(rawNodeToReactFlowNode(
          node,
          region.id,
          {
            x: childX,
            y: padding.top,
          },
        ));
      }
      childX += (nodeWidths[index] || DEFAULT_NODE_WIDTH) + nodeGap;
    });

    cursorY += height + regionGap;
  });

  return {
    rfNodes,
    rfEdges: rawEdges.map(rawEdgeToReactFlowEdge),
  };
}

function regionToReactFlowNode(
  region: Region,
  x: number,
  y: number,
  width: number,
  height: number,
  vocab: ClusterVocab,
  allRegions: Region[],
  accentColor?: string,
  tierBands?: TierBand[],
): Node<ClusterRegionNodeData>[] {
  const parentName = region.cluster?.parent_cluster_id
    ? allRegions.find((candidate) => candidate.cluster?.cluster_id === region.cluster?.parent_cluster_id)?.cluster?.name || null
    : null;
  const memberCount = region.cluster ? region.cluster.member_node_ids.length : region.nodes.length;
  const baseData: ClusterRegionNodeData = {
    cluster: region.cluster,
    vocab,
    memberCount,
    parentName,
    accentColor,
    tierBands,
  };

  // BOTH views split the box so the edges can run between the zebra and the
  // title. BODY (border + fill + zebra) is the parent, BELOW the edges; HEADER
  // (title bar) is a child, ABOVE the edges. React Flow clamps a child's z to
  // max(parent.z, child.z), so the header (region) safely rises above the body
  // (regionBody); a child can never sink below its parent, which is why the
  // zebra must live on the parent. The header is pointer-events:none so clicks
  // fall through to the body and cluster-select still works. (Build and research
  // differ only in the header's visual treatment, keyed off accentColor in
  // ClusterRegionNode — the split itself is identical.)
  const body: Node<ClusterRegionNodeData> = {
    id: region.id,
    type: 'cluster-region',
    position: { x, y },
    data: { ...baseData, layer: 'body', selectionId: region.id },
    style: { width, height },
    selectable: false,
    connectable: false,
    deletable: false,
    zIndex: GRAPH_LAYER.regionBody,
  };
  const header: Node<ClusterRegionNodeData> = {
    id: `${region.id}__header`,
    type: 'cluster-region-header',
    parentId: region.id,
    position: { x: 0, y: 0 },
    data: { ...baseData, layer: 'header', selectionId: region.id },
    style: { width, height: HEADER_LAYER_HEIGHT, pointerEvents: 'none' },
    draggable: false,
    selectable: false,
    connectable: false,
    deletable: false,
    zIndex: GRAPH_LAYER.region,
  };
  return [body, header];
}

function rawNodeToReactFlowNode(
  rawNode: ClusterLayoutRawNode<unknown>,
  parentId: string | undefined,
  position: { x: number; y: number },
): Node {
  return {
    id: rawNode.id,
    type: rawNode.type,
    position,
    ...(parentId ? { parentId, extent: 'parent' as const } : {}),
    data: rawNode.data,
    selected: rawNode.selected,
    hidden: rawNode.hidden,
    className: rawNode.className,
    sourcePosition: rawNode.sourcePosition,
    targetPosition: rawNode.targetPosition,
    style: rawNode.style,
    // Node cards sit on top of everything at rest: above the cluster-region
    // box + tier shading (GRAPH_LAYER.region) and above the edges
    // (GRAPH_LAYER.edgeResting), so connectors never paint over a card.
    zIndex: GRAPH_LAYER.card,
  };
}

function rawEdgeToReactFlowEdge(rawEdge: ClusterLayoutRawEdge<unknown>): Edge {
  return {
    id: rawEdge.id,
    source: rawEdge.source,
    target: rawEdge.target,
    type: rawEdge.type,
    data: rawEdge.data,
    animated: rawEdge.animated,
    hidden: rawEdge.hidden,
    className: rawEdge.className,
    style: rawEdge.style,
    // Render edges BELOW the cluster-region box (and its header text) AND below
    // the node cards, so connectors never paint over the group titles. ReactFlow
    // groups edges into an SVG layer at this zIndex within the viewport stacking
    // context; the zebra tier lanes are translucent (TierLanes) so edges stay
    // visible where they pass behind a shaded tier. Hosts may lift the edges
    // connected to a selection to GRAPH_LAYER.edgeElevated to surface them.
    zIndex: GRAPH_LAYER.edgeResting,
  };
}

export async function buildClusteredElkGraph(
  rawNodes: Array<ClusterLayoutRawNode<unknown>>,
  rawEdges: Array<ClusterLayoutRawEdge<unknown>>,
  clusters: Cluster[],
  options: BuildClusteredElkGraphOptions = {},
): Promise<{ rfNodes: Node[]; rfEdges: Edge[] }> {
  if (rawNodes.length === 0) {
    return { rfNodes: [], rfEdges: [] };
  }

  const resolvedOptions = {
    padding: options.padding || DEFAULT_PADDING,
    nodeNodeSpacing: options.nodeNodeSpacing ?? 120,
    clusterLabelHeight: options.clusterLabelHeight ?? 40,
    elkOptions: options.elkOptions,
    vocab: options.vocab || DEFAULT_VOCAB,
    accentByRegionId: options.accentByRegionId,
    interiorLayout: options.interiorLayout ?? 'flow',
    outerAlignment: options.outerAlignment ?? 'center',
    interClusterLayout: options.interClusterLayout ?? 'sequence',
    stepNumbers: options.stepNumbers ?? false,
  };
  const normalizedClusters = clusters.filter((cluster) => cluster.cluster_id);
  const regions = buildRegions(rawNodes, rawEdges, normalizedClusters);

  if (regions.length === 1) {
    try {
      return await layoutSingleRegion(regions[0], rawEdges, resolvedOptions);
    } catch (error) {
      console.error('[clusterLayout] Single-region ELK failed, using clustered grid fallback:', error);
      return fallbackLayout(regions, rawEdges, resolvedOptions);
    }
  }

  try {
    const regionLayouts = await Promise.all(
      regions.map((region) => layoutRegionInterior(region, rawEdges, resolvedOptions)),
    );
    // Depth-from-source per region, computed ONCE and shared by the outer ELK
    // partitioning (x per tier) and the vertical tier-band stacking (y).
    const regionDepths = resolvedOptions.interClusterLayout === 'tiered'
      ? computeRegionDepths(regionLayouts, rawEdges)
      : null;
    const outerGraph = buildOuterClusterElkGraph(regionLayouts, rawEdges, resolvedOptions, regionDepths);
    const laidOut = await elk.layout(outerGraph);
    const laidOutRegions = laidOut.children || [];
    const rfNodes: Node[] = [];

    // Left anchor: in 'left' mode, the shared left edge that every tier (and the
    // question) snaps to — the leftmost laid-out region's x.
    const leftAnchorX = resolvedOptions.outerAlignment === 'left' && laidOutRegions.length
      ? Math.min(...laidOutRegions.map((r) => r.x || 0))
      : null;

    const isUnsortedById = new Map(regionLayouts.map((rl) => [rl.region.id, rl.region.isUnsorted]));
    const heightById = new Map(regionLayouts.map((rl) => [rl.region.id, rl.height]));

    // Dependency-depth tiers drive three things in tiered mode: vertical
    // stacking, per-tier left-alignment, and per-tier numbering. Build runs in
    // 'sequence' mode (no regionDepths) and falls through to per-region
    // numbering — where each region is its own tier, so it's tier-based anyway.
    let tierTopYByRegion: Map<string, number> | null = null;
    let tierLeftShiftByRegion: Map<string, number> | null = null;
    let stepOrder: Map<string, number> | null = null;

    if (regionDepths) {
      const byTier = new Map<number, { id: string; x: number }[]>();
      laidOutRegions.forEach((r) => {
        const d = regionDepths.get(r.id) ?? 0;
        if (!byTier.has(d)) byTier.set(d, []);
        byTier.get(d)!.push({ id: r.id, x: r.x || 0 });
      });
      const depthsAsc = Array.from(byTier.keys()).sort((a, b) => a - b);

      // Vertical: top-align every box in a tier; stack each tier strictly below
      // the tallest box of the one above (gap = OUTER_TIER_GAP). ELK's own
      // per-tier centering leaves ragged tops and lets tall boxes bleed down.
      tierTopYByRegion = new Map();
      let cursor = 0;
      depthsAsc.forEach((d) => {
        const ids = byTier.get(d)!.map((m) => m.id);
        ids.forEach((id) => tierTopYByRegion!.set(id, cursor));
        cursor += Math.max(...ids.map((id) => heightById.get(id) ?? DEFAULT_CLUSTER_HEIGHT)) + OUTER_TIER_GAP;
      });

      // Left: shift each tier as a unit so its leftmost box sits at the anchor,
      // preserving the horizontal spread within the tier. The question's own
      // single-box tier shifts to the anchor too, so its "Q" lines up.
      if (leftAnchorX != null) {
        tierLeftShiftByRegion = new Map();
        depthsAsc.forEach((d) => {
          const members = byTier.get(d)!;
          const tierMinX = Math.min(...members.map((m) => m.x));
          members.forEach((m) => tierLeftShiftByRegion!.set(m.id, leftAnchorX - tierMinX));
        });
      }

      // Numbering: ONE numeral per tier (the question's tier excluded → it gets
      // "Q"), ranked top→bottom, carried by that tier's leftmost cluster box so
      // every numeral lines up in the gutter. All clusters in a tier share it.
      if (resolvedOptions.stepNumbers) {
        stepOrder = new Map();
        let tierNum = 0;
        depthsAsc.forEach((d) => {
          const clusters = byTier.get(d)!.filter((m) => !isUnsortedById.get(m.id));
          if (clusters.length === 0) return;
          tierNum += 1;
          const leftmost = clusters.reduce((a, b) => (a.x <= b.x ? a : b));
          stepOrder!.set(leftmost.id, tierNum);
        });
      }
    } else if (resolvedOptions.stepNumbers) {
      // Sequence (build): one numeral per region, ranked top→bottom.
      stepOrder = new Map(
        [...laidOutRegions]
          .filter((r) => !isUnsortedById.get(r.id))
          .sort((a, b) => (a.y || 0) - (b.y || 0))
          .map((r, index) => [r.id, index + 1] as const),
      );
    }

    // Center mode only: place the unclustered QUESTION over the cluster bbox so
    // it sits mid-constellation. In 'left' mode the question rides its tier's
    // left-shift instead (above), so this is skipped. The top source is detected
    // from PRE-LAYOUT order (buildRegions puts the unsorted region first only
    // when it's a pure source); a laid-out-y check is unreliable because ELK can
    // float a no-incoming cluster onto the question's top layer.
    let unsortedTopBaseX: number | null = null;
    const unsortedIsTopSource = regionLayouts.length > 1 && regionLayouts[0].region.isUnsorted;
    if (unsortedIsTopSource && leftAnchorX == null) {
      const unsortedLaid = laidOutRegions.find((r) => isUnsortedById.get(r.id));
      const clusterLaid = laidOutRegions.filter((r) => !isUnsortedById.get(r.id));
      if (unsortedLaid && clusterLaid.length > 0) {
        const minX = Math.min(...clusterLaid.map((r) => r.x || 0));
        const maxX = Math.max(...clusterLaid.map((r) => (r.x || 0) + (r.width || 0)));
        unsortedTopBaseX = (minX + maxX) / 2 - (unsortedLaid.width || 0) / 2;
      }
    }

    laidOutRegions.forEach((laidRegion) => {
      const regionLayout = regionLayouts.find((candidate) => candidate.region.id === laidRegion.id);
      if (!regionLayout) {
        return;
      }
      const { region } = regionLayout;
      // Tiered + left: shift the box by its tier's left-shift (keeps intra-tier
      // spread). Sequence + left: collapse to the single anchor column. Center
      // mode: question over the bbox, clusters at ELK's x.
      const tierShift = tierLeftShiftByRegion?.get(region.id);
      const baseX = tierShift != null
        ? (laidRegion.x || 0) + tierShift
        : leftAnchorX != null
          ? leftAnchorX
          : region.isUnsorted && unsortedTopBaseX !== null
            ? unsortedTopBaseX
            : (laidRegion.x || 0);
      const baseY = tierTopYByRegion?.get(region.id) ?? (laidRegion.y || 0);

      if (!region.isUnsorted) {
        const headerHeight = Math.max(resolvedOptions.padding.top, resolvedOptions.clusterLabelHeight + 8);
        const tierBands = resolvedOptions.interiorLayout === 'tiered'
          ? computeTierBands(region, regionLayout.children, regionLayout.height, headerHeight)
          : undefined;
        rfNodes.push(...regionToReactFlowNode(
          region,
          baseX,
          baseY,
          laidRegion.width || CLUSTER_FALLBACK_MIN_WIDTH,
          laidRegion.height || DEFAULT_CLUSTER_HEIGHT,
          resolvedOptions.vocab,
          regions,
          resolvedOptions.accentByRegionId?.[region.id],
          tierBands,
        ));

        const stepNumber = stepOrder?.get(region.id);
        if (stepNumber != null) {
          // Attach the numeral to its region as a CHILD node so dragging the
          // cluster box carries its step number along (the members already
          // follow via parentId). Position is relative to the region's
          // top-left; the negative x places it in the gutter to the LEFT of
          // the box. Deliberately no `extent: 'parent'` — that would clamp the
          // child inside the box bounds, but the numeral must sit outside it.
          rfNodes.push({
            id: `__step_${region.id}`,
            type: 'step-number',
            parentId: region.id,
            position: {
              x: -(STEP_NUMBER_GUTTER + STEP_NUMBER_MARGIN),
              y: (laidRegion.height || DEFAULT_CLUSTER_HEIGHT) / 2 - STEP_NUMBER_SIZE / 2,
            },
            data: { number: stepNumber },
            draggable: false,
            selectable: false,
            connectable: false,
            deletable: false,
            // Gutter numeral rides at the region band — above resting edges,
            // below cards — same as the region box it labels.
            zIndex: GRAPH_LAYER.region,
          });
        }
      }

      regionLayout.children.forEach((laidNode, childIndex) => {
        const rawNode = region.nodes.find((candidate) => candidate.id === laidNode.id);
        if (!rawNode) {
          return;
        }
        if (region.isUnsorted) {
          // In 'left' mode snap the question's left edge to the shared anchor
          // (drop the interior pad offset) so its "Q" gutter glyph aligns
          // exactly with the cluster numerals; 'center' mode keeps the offset.
          const unsortedX = leftAnchorX != null ? baseX : baseX + (laidNode.x || 0);
          rfNodes.push(rawNodeToReactFlowNode(rawNode, undefined, {
            x: unsortedX,
            y: baseY + (laidNode.y || 0),
          }));
          // Mark the framing QUESTION (the top, unclustered source) with a "Q"
          // gutter glyph — the research analogue of the cluster step numerals,
          // so the left column reads Q → 1 → 2 → 3. Attached as a CHILD of the
          // question node so it rides along when the (now movable) question is
          // dragged, and uses the same gutter offset as the cluster numerals so
          // it lines up directly above step 1. Only the top source's first node
          // gets it (in practice the lone framing question).
          if (resolvedOptions.stepNumbers && unsortedIsTopSource && childIndex === 0) {
            rfNodes.push({
              id: `__step_${rawNode.id}`,
              type: 'step-number',
              parentId: rawNode.id,
              position: {
                x: -(STEP_NUMBER_GUTTER + STEP_NUMBER_MARGIN),
                y: (rawNode.height || DEFAULT_NODE_HEIGHT) / 2 - STEP_NUMBER_SIZE / 2,
              },
              data: { number: 'Q' },
              draggable: false,
              selectable: false,
              connectable: false,
              deletable: false,
              zIndex: GRAPH_LAYER.region,
            });
          }
        } else {
          rfNodes.push(rawNodeToReactFlowNode(rawNode, region.id, {
            x: laidNode.x || 0,
            y: laidNode.y || 0,
          }));
        }
      });
    });

    return {
      rfNodes,
      rfEdges: rawEdges.map(rawEdgeToReactFlowEdge),
    };
  } catch (error) {
    console.error('[clusterLayout] ELK failed, using clustered grid fallback:', error);
    return fallbackLayout(regions, rawEdges, resolvedOptions);
  }
}
